<template>
  <div
    class="app-shell"
    :class="{ 'app-shell--fixed-work-area': route.meta.fixedWorkArea }"
  >
    <NavBar :class="{ 'app-shell__bar': route.meta.fixedWorkArea }" />
    <LoadingSpinner />
    <div :class="route.meta.fixedWorkArea ? 'app-shell__route-frame' : 'contents'">
      <router-view />
    </div>
    <AppFooter :class="{ 'app-shell__bar': route.meta.fixedWorkArea }" />
  </div>
</template>

<script>
import NavBar from '@/ReusableComponents/NavBar.vue';
import AppFooter from '@/ReusableComponents/AppFooter.vue';
import LoadingSpinner from '@/ReusableComponents/LoadingSpinner.vue';
import { useAuthStore } from '@/pinia/authStore';
import { useDsaReportsStore } from '@/pinia/dsaReportsStore';
import { useRoute } from 'vue-router';

const SLEEK_PRODUCT_ID = 949616259;
const SLEEK_SDK_ID = 'sleekplan-jssdk';
const SLEEK_SDK_SRC = 'https://client.sleekplan.com/sdk/e.js';
let sleekLoaded = false;

export default {
  components: { NavBar, LoadingSpinner, AppFooter },
  setup() {
    const authStore = useAuthStore();
    const myStore = useDsaReportsStore();
    const route = useRoute();
    return { authStore, myStore, route };
  },
  watch: {
    'authStore.isAuthenticated'(isAuthed) {
      if (isAuthed) {
        this.initSleekplan();
        if (!this.myStore.email) {
          this.myStore.fetchUserData();
        }
      } else {
        this.teardownSleekplan();
      }
    }
  },
  mounted() {
    if (this.authStore?.isAuthenticated) {
      this.initSleekplan();
      if (!this.myStore.email) {
        this.myStore.fetchUserData();
      }
    } else {
      this.teardownSleekplan();
    }
  },
  methods: {
    initSleekplan() {
      this.$nextTick(() => {
        if (typeof window === 'undefined') return;
        if (sleekLoaded) return;

        if (!window.$sleek) {
          window.$sleek = [];
        }
        window.SLEEK_PRODUCT_ID = SLEEK_PRODUCT_ID;

        if (!document.getElementById(SLEEK_SDK_ID)) {
          const script = document.createElement('script');
          script.id = SLEEK_SDK_ID;
          script.async = true;
          script.src = SLEEK_SDK_SRC;
          document.head.appendChild(script);
        }
        sleekLoaded = true;
        this.setupSleekplanLauncherDrag();
      });
    },
    setupSleekplanLauncherDrag() {
      if (typeof window === 'undefined') return;
      const storageKey = 'sleekplanLauncherTopPct';
      const minPct = 10;
      const maxPct = 90;

      const applyPosition = (button) => {
        const stored = Number(window.localStorage.getItem(storageKey));
        const pct = Number.isFinite(stored) ? stored : 80;
        button.style.position = 'fixed';
        button.style.top = `${Math.min(maxPct, Math.max(minPct, pct))}%`;
        button.style.bottom = 'auto';
      };

      const attachDrag = (button) => {
        if (button.dataset.dragReady === 'true') return;
        button.dataset.dragReady = 'true';
        applyPosition(button);

        let isDragging = false;

        const onPointerMove = (event) => {
          if (!isDragging) return;
          const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 1;
          const pct = Math.min(maxPct, Math.max(minPct, (event.clientY / viewportHeight) * 100));
          button.style.top = `${pct}%`;
          window.localStorage.setItem(storageKey, String(Math.round(pct)));
        };

        const onPointerUp = () => {
          if (!isDragging) return;
          isDragging = false;
          window.removeEventListener('pointermove', onPointerMove);
          window.removeEventListener('pointerup', onPointerUp);
        };

        button.addEventListener('pointerdown', (event) => {
          if (event.button !== 0) return;
          isDragging = true;
          window.addEventListener('pointermove', onPointerMove);
          window.addEventListener('pointerup', onPointerUp);
        });
      };

      const tryAttach = () => {
        const button = document.querySelector('.i-sl-button');
        if (button) {
          attachDrag(button);
          return true;
        }
        return false;
      };

      if (tryAttach()) return;

      let attempts = 0;
      const interval = window.setInterval(() => {
        attempts += 1;
        if (tryAttach() || attempts > 40) {
          window.clearInterval(interval);
        }
      }, 250);
    },
    teardownSleekplan() {
      if (typeof document === 'undefined') return;
      const script = document.getElementById(SLEEK_SDK_ID);
      if (script && script.parentNode) {
        script.parentNode.removeChild(script);
      }
      document
        .querySelectorAll(
          '[id*="sleek"], [class*="sleek"], [data-sleek], [data-sleekplan], iframe[src*="sleek"]'
        )
        .forEach((node) => {
          if (node.parentNode) {
            node.parentNode.removeChild(node);
          } else if (node.style) {
            node.style.display = 'none';
          }
        });
      if (typeof window !== 'undefined') {
        delete window.$sleek;
        delete window.SLEEK_PRODUCT_ID;
      }
      sleekLoaded = false;
    }
  }
};
</script>

<style src="./assets/tailwind.css" />

<style>
html,
body,
#app {
  height: 100%;
}

.app-shell {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.app-shell--fixed-work-area {
  position: fixed;
  inset: 0;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  width: 100%;
  height: 100vh;
  min-height: 0;
  overflow: hidden;
}

.app-shell__bar {
  min-height: 0;
}

.app-shell__route-frame {
  display: flex;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
}

.app-shell--fixed-work-area .app-shell__route-frame > * {
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
}

@media print {
  textarea {
    white-space: pre-wrap !important;
    overflow-wrap: break-word !important;
    word-break: break-word !important;
  }
}

#sleekplan-launcher,
.sleekplan-launcher,
[class*="sleekplan-launcher"],
iframe[src*="sleekplan"] {
  bottom: 1rem !important;
}

.i-sl-button[class*='-middle'] {
  top: 80%;
  bottom: auto;
  cursor: grab;
}


</style>
